import React, { useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button, Container, Divider, Alert } from '@mui/material';
import { Google, Microsoft, Facebook, Apple } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// TypeScript declarations for Google and Microsoft APIs
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: any) => void }) => void;
          prompt: () => void;
        };
      };
    };
    msal?: any;
  }
}

const LoginPage: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleLogin = useCallback(async () => {
    setError(null);
    setLoading('google');
    
    try {
      // Load Google Sign-In script
      if (!window.google) {
        await loadGoogleScript();
      }

      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      
      if (!clientId) {
        throw new Error('Google Client ID not configured');
      }
      
      window.google!.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleResponse,
      });

      window.google!.accounts.id.prompt();
    } catch (error) {
      console.error('Google login error:', error);
      setError('Failed to initialize Google login');
    } finally {
      setLoading(null);
    }
  }, []);

  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.getElementById('google-script')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google script'));
      document.head.appendChild(script);
    });
  };

  const handleGoogleResponse = async (response: any) => {
    try {
      setLoading('google');
      setError(null);

      // Send the credential token to the backend using the correct API method
      const result = await api.loginWithGoogle(response.credential);
      
      // Use the login function from AuthContext to properly set authentication state
      await login(result.token, result.user);
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleMicrosoftLogin = useCallback(async () => {
    setError(null);
    setLoading('microsoft');
    
    try {
      // Load Microsoft MSAL script if not already loaded
      if (!window.msal) {
        await loadMicrosoftScript();
      }

      const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
      
      if (!clientId) {
        throw new Error('Microsoft Client ID not configured');
      }

      // Initialize MSAL instance
      const msalConfig = {
        auth: {
          clientId: clientId,
          authority: 'https://login.microsoftonline.com/common',
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      };

      const msalInstance = new window.msal.PublicClientApplication(msalConfig);
      await msalInstance.initialize();

      // Request login
      const loginRequest = {
        scopes: ['openid', 'profile', 'email'],
      };

      const response = await msalInstance.loginPopup(loginRequest);
      
      if (response.accessToken) {
        // Send the access token to the backend
        const result = await api.loginWithMicrosoft(response.accessToken);
        
        // Use the login function from AuthContext to properly set authentication state
        await login(result.token, result.user);
        
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Microsoft login error:', error);
      setError('Microsoft authentication failed. Please try again.');
    } finally {
      setLoading(null);
    }
  }, [login, navigate]);

  const loadMicrosoftScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (document.getElementById('microsoft-script')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'microsoft-script';
      script.src = 'https://alcdn.msauth.net/browser/2.14.2/js/msal-browser.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Microsoft script'));
      document.head.appendChild(script);
    });
  };

  const handleProviderLogin = async (provider: string) => {
    if (provider === 'google') {
      return handleGoogleLogin();
    }
    if (provider === 'microsoft') {
      return handleMicrosoftLogin();
    }
    
    setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} login is not yet implemented`);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom align="center">
            Accountability Compass
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            A personal reflection tool for growth and accountability
          </Typography>

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Four-Quadrant Reflection
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • What happened?<br />
              • What did I do or say?<br />
              • How did it affect others?<br />
              • What can I do to make it better?
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Sign in with your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Google />}
            onClick={() => handleProviderLogin('google')}
            sx={{
              mt: 1,
              mb: 2,
              py: 1.5,
              bgcolor: '#4285f4',
              '&:hover': {
                bgcolor: '#357ae8',
              },
            }}
          >
            Continue with Google
          </Button>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Microsoft />}
            onClick={() => handleProviderLogin('microsoft')}
            sx={{
              mb: 2,
              py: 1.5,
              bgcolor: '#0078d4',
              '&:hover': {
                bgcolor: '#106ebe',
              },
            }}
          >
            Continue with Microsoft
          </Button>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Facebook />}
            onClick={() => handleProviderLogin('facebook')}
            sx={{
              mb: 2,
              py: 1.5,
              bgcolor: '#1877f2',
              '&:hover': {
                bgcolor: '#166fe5',
              },
            }}
          >
            Continue with Facebook
          </Button>

          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<Apple />}
            onClick={() => handleProviderLogin('apple')}
            sx={{
              mb: 3,
              py: 1.5,
              bgcolor: '#000000',
              '&:hover': {
                bgcolor: '#333333',
              },
            }}
          >
            Continue with Apple
          </Button>

          <Divider sx={{ width: '100%', mb: 2 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            Secure authentication with your preferred provider
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
